<script setup lang="ts">
import { SEARCH_SUGGEST_MAX_QUERY_LENGTH, SEARCH_SUGGEST_MIN_QUERY_LENGTH } from '@lib/shared';

import { SellingMethod } from '~/apiClient';
import { type PlatformSellingMethod } from '~/types/sale.type';

// PROPS
interface Props {
    preview: QuickSearchPreviewState;
    isLoading?: boolean;
    /** Floats the preview over what follows; the hero must not reflow. */
    hasFloatingPreview?: boolean;
    /** Shows the "Esc closes" hint — only the modal mount closes on Esc, and only with a keyboard; inline mounts just drop the preview. */
    hasDismissHint?: boolean;
    hasAutofocus?: boolean;
}

const { preview, isLoading = false, hasFloatingPreview = true, hasDismissHint = false, hasAutofocus = false } = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    submit: [];
    navigate: [];
}>();

// MODELS
const inputValue = defineModel<string>({ required: true });
const sellingMethod = defineModel<PlatformSellingMethod>('sellingMethod', { required: true });

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const hintId = useId();
// Includes the tabs: switching channel must re-slice the preview, not dismiss it.
const panelRef = useTemplateRef('panelRef');
const textarea = useTemplateRef('textarea');
const previewRef = useTemplateRef('previewRef');

const isPreviewDismissed = ref(false);

const isSubmitDisabled = computed<boolean>(() => isLoading || inputValue.value.trim().length < SEARCH_SUGGEST_MIN_QUERY_LENGTH);

// Empty and error are preview states too: kept in the floating panel they cannot reflow the hero.
const isPreviewOpen = computed<boolean>(
    () => !isPreviewDismissed.value && (!!preview.items.length || preview.isPending || preview.isEmpty || preview.isError)
);

const emptyMessage = computed<string>(() => tt(`search.quick.preview.empty.${sellingMethod.value}`, { query: preview.query }));

const otherSellingMethod = computed<PlatformSellingMethod>(() =>
    sellingMethod.value === SellingMethod.Auction ? SellingMethod.DirectSale : SellingMethod.Auction
);

// `(N)` after the tab's own label: the count needs no plural forms across the locales.
const otherChannelLabel = computed<string>(() => {
    if (!preview.otherChannelTotal) return '';

    const label = otherSellingMethod.value === SellingMethod.Auction ? tt('search.quick.tab.auction') : tt('search.quick.tab.directSale');

    return `${label} (${preview.otherChannelTotal})`;
});

// "Try fewer words" is wrong advice when the other tab has matches: point there instead.
const emptyFollowUp = computed<string>(() =>
    otherChannelLabel.value ? `${tt('search.quick.preview.empty.otherChannel')} ${otherChannelLabel.value}` : tt('search.quick.preview.empty.hint')
);

// The visible hints are `aria-hidden`, so these two carry the whole assistive channel.
const describedByMessage = computed<string>(() =>
    [tt('search.quick.hint.submit', { key: tt('search.quick.hint.submitKey') }), hasDismissHint ? tt('search.quick.hint.dismiss') : '']
        .filter(Boolean)
        .join(' · ')
);

const statusMessage = computed<string>(() => {
    if (isLoading) return tt('search.quick.loading');
    if (preview.isStale) return '';
    if (preview.isError) return tt('search.quick.preview.error');
    if (preview.isEmpty) return `${emptyMessage.value} ${emptyFollowUp.value}`;

    return preview.items.length ? tt('search.quick.preview.announce', { total: preview.total }) : '';
});

// HANDLERS
const onInputActivity = (): void => {
    isPreviewDismissed.value = false;
};

// Tabbing past the panel must not leave the floating preview covering the focused element (WCAG 2.4.11).
// A null `relatedTarget` is ignored: Safari does not focus links or buttons on click, and a focused row unmounts
// on refresh — both blur to nothing while the pointer is still inside. Pointer exits stay with `onClickOutside`.
// In the modal the preview is in flow and covers nothing, so tabbing around the focus trap leaves it up.
const onPanelFocusout = (event: FocusEvent): void => {
    if (!hasFloatingPreview) return;

    const next = event.relatedTarget;

    if (next instanceof Node && !panelRef.value?.contains(next)) isPreviewDismissed.value = true;
};

// Only the floating preview: in the modal it is in flow, and Reka closes the whole dialog on Esc (no `stopPropagation`
// here), so dismissing it too would just shrink the card during the close animation.
// A focused row unmounts with the preview, so focus returns to the field first; tabs and submit stay mounted and keep
// it. Order matters: the synchronous `focusin` would otherwise reopen the preview.
// Like Enter, an Esc that cancels an IME composition is not meant for us.
const onEscape = (event: KeyboardEvent): void => {
    if (!hasFloatingPreview || event.isComposing || !isPreviewOpen.value) return;

    const previewElement: unknown = previewRef.value?.$el;

    if (previewElement instanceof HTMLElement && previewElement.contains(document.activeElement)) textarea.value?.textareaRef?.focus();
    isPreviewDismissed.value = true;
};

// The button unmounts with the empty state, so focus moves to the tab it just selected: it stays in the panel and tells
// assistive tech which channel is up. Not the field: on touch that would pop the keyboard over the rows asked for.
const onSwitchChannel = async (): Promise<void> => {
    sellingMethod.value = otherSellingMethod.value;
    await nextTick();
    panelRef.value?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus();
};

// An IME confirming a composition fires Enter too; that must not submit.
const onEnter = (event: KeyboardEvent): void => {
    if (!event.isComposing) emit('submit');
};

// LIFECYCLE
onClickOutside(panelRef, () => {
    isPreviewDismissed.value = true;
});
</script>

<template>
    <div
        ref="panelRef"
        class="relative flex w-full flex-col items-center"
        @focusin="onInputActivity"
        @focusout="onPanelFocusout"
        @keydown.esc="onEscape"
    >
        <QuickSearchMethodTabs v-model="sellingMethod" class="relative z-10" />

        <div class="bg-default ring-default focus-within:ring-primary -mt-6 w-full rounded-2xl p-4 shadow-lg ring-1 md:p-6">
            <UTextarea
                ref="textarea"
                v-model="inputValue"
                :autofocus="hasAutofocus"
                :rows="2"
                :maxrows="6"
                :maxlength="SEARCH_SUGGEST_MAX_QUERY_LENGTH"
                autoresize
                autocomplete="off"
                variant="none"
                :aria-label="tt('search.quick.aria.input')"
                :aria-describedby="hintId"
                :placeholder="tt('search.quick.placeholder')"
                :ui="{ base: 'p-0 pt-4 text-base md:text-lg' }"
                @keydown.enter.exact.prevent="onEnter"
                @update:model-value="onInputActivity"
            />

            <div class="mt-4 flex items-end justify-between gap-4">
                <div>
                    <!-- Stacked in one cell and toggled with `invisible`: the row keeps the taller hint's height whichever is up. -->
                    <div class="grid">
                        <p
                            class="text-muted col-start-1 row-start-1 flex items-center gap-2 text-sm"
                            :class="{ invisible: !isLoading }"
                            aria-hidden="true"
                        >
                            <UIcon name="i-svg-spinners-270-ring" class="size-4 shrink-0" />
                            <span>{{ tt('search.quick.loading') }}</span>
                        </p>

                        <p class="text-muted col-start-1 row-start-1 text-sm" :class="{ invisible: isLoading }" aria-hidden="true">
                            <BaseI18nT keypath="search.quick.hint.submit" tag="span">
                                <template #key>
                                    <span class="text-primary font-medium">{{ tt('search.quick.hint.submitKey') }}</span>
                                </template>
                            </BaseI18nT>
                            <span v-if="hasDismissHint" class="text-dimmed"> · {{ tt('search.quick.hint.dismiss') }}</span>
                        </p>
                    </div>

                    <span :id="hintId" class="sr-only">{{ describedByMessage }}</span>
                    <div role="status" class="sr-only">{{ statusMessage }}</div>
                </div>

                <UButton
                    icon="lucide:arrow-right"
                    square
                    :disabled="isSubmitDisabled"
                    :aria-label="tt('search.quick.aria.submit')"
                    :ui="{ base: 'size-14 shrink-0 justify-center rounded-xl' }"
                    @click="emit('submit')"
                />
            </div>
        </div>

        <QuickSearchPreview
            v-if="isPreviewOpen"
            ref="previewRef"
            :items="preview.items"
            :total="preview.total"
            :is-pending="preview.isPending"
            :is-stale="preview.isStale"
            :is-empty="preview.isEmpty"
            :is-error="preview.isError"
            :empty-message="emptyMessage"
            :other-channel-label="otherChannelLabel"
            :selling-method="sellingMethod"
            :is-floating="hasFloatingPreview"
            @navigate="emit('navigate')"
            @see-all="emit('submit')"
            @switch-channel="onSwitchChannel"
        />
    </div>
</template>
